import { getLanguageCode } from '../utilities';

export default class MediaLibraryClient
{
    constructor()
    {
        let languageCode = getLanguageCode();

        this._searchUri = '/v1-api-media-library-search-' + languageCode;
        this._downloadFileUri = '/v1-api-media-library-file-download-' + languageCode;
        this._downloadArchiveUri = '/v1-api-media-library-archive-download-' + languageCode;
    }

    /**
	 * POST
	 */

    /**
	 * @param {boolean} newFilter
	 * @param {boolean} videoFilter
	 * @param {boolean} threeSixtyFilter
	 * @param {boolean} logoFilter
	 * @param {number} assetsPerPage
	 * @param {number} skipAssets
	 * @returns {Promise<Response>}
	 */
    fetchAllAssets(
        newFilter,
        videoFilter,
        threeSixtyFilter,
        logoFilter,
        assetsPerPage,
        skipAssets
    )
    {
        return fetch(this._searchUri, {
            'method': 'POST',
            'headers': {
                'Content-Type': 'application/json'
            },
            'body': JSON.stringify({
                'track_total_hits': true,
                'query': {
                    'bool': {
                        'filter': [
                            ...(newFilter ? [{
                                'range': {
                                    'firstAddedToSearchIndexDatetime': {
                                        'gte': 'now-2592000s', // 30 days in seconds
                                        'lte': 'now'
                                    }
                                }
                            }] : []),
                            ...(videoFilter ? [{
                                'term': {
                                    'assetClass': 'video'
                                }
                            }] : []),
                            ...(threeSixtyFilter ? [{
                                'term': {
                                    'assetClass': 'archive'
                                }
                            }] : []),
                            ...(logoFilter ? [{
                                'term': {
                                    'assetType.keyword': 'label-image'
                                }
                            }] : [])
                        ],
                        'should': [
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetType.keyword': 'product-image'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.png-max'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.jpeg-max'
                                            }
                                        }
                                    ]
                                }
                            },
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetClass': 'video'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.none'
                                            }
                                        }
                                    ]
                                }
                            },
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetClass': 'archive'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.none'
                                            }
                                        }
                                    ]
                                }
                            },
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetType.keyword': 'label-image'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.none'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.vector-eps'
                                            }
                                        }
                                    ]
                                }
                            }
                        ],
                        'minimum_should_match': 1
                    }
                },
                'aggs': {
                    'new': {
                        'filter': {
                            'range': {
                                'firstAddedToSearchIndexDatetime': {
                                    'gte': 'now-2592000s' // 30 days in seconds
                                }
                            }
                        }
                    },
                    'videos': {
                        'filter': {
                            'term': {
                                'assetClass': 'video'
                            }
                        }
                    },
                    'archives': {
                        'filter': {
                            'term': {
                                'assetClass': 'archive'
                            }
                        }
                    },
                    'logos': {
                        'filter': {
                            'term': {
                                'assetType.keyword': 'label-image'
                            }
                        }
                    }
                },
                'size': assetsPerPage,
                'from': skipAssets,
                'sort': [
                    {
                        'firstAddedToSearchIndexDatetime':
                            {
                                'unmapped_type': 'keyword',
                                'order': 'desc'
                            }
                    }
                ]
            })
        });
    }

    /**
	 * @param {String} searchField
	 * @param {String} searchValue
	 * @param {boolean} newFilter
	 * @param {boolean} videoFilter
	 * @param {boolean} threeSixtyFilter
	 * @param {boolean} logoFilter
	 * @param {number} assetsPerPage
	 * @param {number} skipAssets
	 * @returns {Promise<Response>}
	 */
    fetchAssetsBySearchTerm(
        searchField,
        searchValue,
        newFilter,
        videoFilter,
        threeSixtyFilter,
        logoFilter,
        assetsPerPage,
        skipAssets
    )
    {
        return fetch(this._searchUri, {
            'method': 'POST',
            'headers': {
                'Content-Type': 'application/json'
            },
            'body': JSON.stringify({
                'track_total_hits': true,
                'query': {
                    'bool': {
                        'must': [
                            ...(searchField === 'text' ? [{
                                'bool': {
                                    'should': [
                                        {
                                            'multi_match': {
                                                'query': searchValue,
                                                'fields': ['label^5', 'labels^5']
                                            }
                                        },
                                        {
                                            'multi_match': {
                                                'query': searchValue,
                                                'fields': ['label.plain^2', 'labels.plain^2'],
                                                'fuzziness': 'auto'
                                            }
                                        },
                                        {
                                            'multi_match': {
                                                'query': searchValue,
                                                'fields': ['title^1.2']
                                            }
                                        },
                                        {
                                            'term': { 'label.keyword':  { 'value': searchValue, 'boost': 10 } }
                                        },
                                        {
                                            'term': { 'labels.keyword':  { 'value': searchValue, 'boost': 10 } }
                                        }
                                    ],
                                    'minimum_should_match': 1
                                }
                            }] : []),
                        ],
                        'filter': [
                            ...(searchField === 'eanCode' ? [{
                                'term': {
                                    'eanCode': searchValue
                                }
                            }] : []),
                            ...(searchField === 'productNumber' ? [{
                                'wildcard': {
                                    'productNumbers': {
                                        'value': '*' + searchValue + '*'
                                    }
                                }
                            }] : []),
                            ...(newFilter ? [{
                                'range': {
                                    'firstAddedToSearchIndexDatetime': {
                                        'gte': 'now-2592000s', // 30 days in seconds
                                        'lte': 'now'
                                    }
                                }
                            }] : []),
                            ...(videoFilter ? [{
                                'term': {
                                    'assetClass': 'video'
                                }
                            }] : []),
                            ...(threeSixtyFilter ? [{
                                'term': {
                                    'assetClass': 'archive'
                                }
                            }] : []),
                            ...(logoFilter ? [{
                                'term': {
                                    'assetType.keyword': 'label-image'
                                }
                            }] : [])
                        ],
                        'should': [
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetType.keyword': 'product-image'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.png-max'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.jpeg-max'
                                            }
                                        }
                                    ]
                                }
                            },
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetClass': 'video'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.none'
                                            }
                                        }
                                    ]
                                }
                            },
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetClass': 'archive'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.none'
                                            }
                                        }
                                    ]
                                }
                            },
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetType.keyword': 'label-image'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.none'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.vector-eps'
                                            }
                                        }
                                    ]
                                }
                            }
                        ],
                        'minimum_should_match': 1
                    }
                },
                'aggs': {
                    'new': {
                        'filter': {
                            'range': {
                                'firstAddedToSearchIndexDatetime': {
                                    'gte': 'now-2592000s'  // 30 days in seconds
                                }
                            }
                        }
                    },
                    'videos': {
                        'filter': {
                            'term': {
                                'assetClass': 'video'
                            }
                        }
                    },
                    'archives': {
                        'filter': {
                            'term': {
                                'assetClass': 'archive'
                            }
                        }
                    },
                    'logos': {
                        'filter': {
                            'term': {
                                'assetType.keyword': 'label-image'
                            }
                        }
                    }
                },
                'size': assetsPerPage,
                'from': skipAssets,
                'sort': [
                    {
                        '_score': 'desc',
                        'firstAddedToSearchIndexDatetime':
                            {
                                'unmapped_type': 'keyword',
                                'order': 'desc'
                            }
                    }
                ]
            })
        });
    }

    /**
	 * @param {Array} eanBatch
	 * @param {Array} productNumberBatch
	 * @param {boolean} newFilter
	 * @param {boolean} videoFilter
	 * @param {boolean} threeSixtyFilter
	 * @param {boolean} logoFilter
	 * @param {number} assetsPerPage
	 * @param {number} skipAssets
	 * @returns {Promise<Response>}
	 */
    fetchAssetsByBatchSearch(
        eanBatch,
        productNumberBatch,
        newFilter,
        videoFilter,
        threeSixtyFilter,
        logoFilter,
        assetsPerPage,
        skipAssets
    )
    {
        return fetch(this._searchUri, {
            'method': 'POST',
            'headers': {
                'Content-Type': 'application/json'
            },
            'body': JSON.stringify({
                'track_total_hits': true,
                'query': {
                    'bool': {
                        'must': [
                            {
                                'bool': {
                                    'should': [
                                        ...(eanBatch.length ? [{
                                            'terms': {
                                                'eanCode': eanBatch
                                            }
                                        }] : []),
                                        ...(productNumberBatch.length ? [{
                                            'bool': {
                                                'should': productNumberBatch.map(productNumber => ({
                                                    'wildcard': {
                                                        'productNumbers': {
                                                            'value': '*' + productNumber + '*'
                                                        }
                                                    }
                                                }))
                                            }
                                        }] : [])
                                    ],
                                    'minimum_should_match': eanBatch.length || productNumberBatch.length ? 1 : 0,
                                }
                            },
                            {
                                'bool': {
                                    'should': [
                                        {
                                            'bool': {
                                                'filter': [
                                                    {
                                                        'term': {
                                                            'assetType.keyword': 'product-image'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.thumbnail-200'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.png-max'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.jpeg-max'
                                                        }
                                                    }
                                                ]
                                            }
                                        },
                                        {
                                            'bool': {
                                                'filter': [
                                                    {
                                                        'term': {
                                                            'assetClass': 'video'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.thumbnail-200'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.none'
                                                        }
                                                    }
                                                ]
                                            }
                                        },
                                        {
                                            'bool': {
                                                'filter': [
                                                    {
                                                        'term': {
                                                            'assetClass': 'archive'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.thumbnail-200'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.none'
                                                        }
                                                    }
                                                ]
                                            }
                                        },
                                        {
                                            'bool': {
                                                'filter': [
                                                    {
                                                        'term': {
                                                            'assetType.keyword': 'label-image'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.thumbnail-200'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.none'
                                                        }
                                                    },
                                                    {
                                                        'exists': {
                                                            'field': 'assetFiles.vector-eps'
                                                        }
                                                    }
                                                ]
                                            }
                                        }
                                    ],
                                    'minimum_should_match': 1
                                }
                            }
                        ],
                        'filter': [
                            ...(newFilter ? [{
                                'range': {
                                    'firstAddedToSearchIndexDatetime': {
                                        'gte': 'now-2592000s', // 30 days in seconds
                                        'lte': 'now'
                                    }
                                }
                            }] : []),
                            ...(videoFilter ? [{
                                'term': {
                                    'assetClass': 'video'
                                }
                            }] : []),
                            ...(threeSixtyFilter ? [{
                                'term': {
                                    'assetClass': 'archive'
                                }
                            }] : []),
                            ...(logoFilter ? [{
                                'term': {
                                    'assetType.keyword': 'label-image'
                                }
                            }] : [])
                        ]
                    }
                },
                'aggs': {
                    'new': {
                        'filter': {
                            'range': {
                                'firstAddedToSearchIndexDatetime': {
                                    'gte': 'now-2592000s'  // 30 days in seconds
                                }
                            }
                        }
                    },
                    'videos': {
                        'filter': {
                            'term': {
                                'assetClass': 'video'
                            }
                        }
                    },
                    'archives': {
                        'filter': {
                            'term': {
                                'assetClass': 'archive'
                            }
                        }
                    },
                    'logos': {
                        'filter': {
                            'term': {
                                'assetType.keyword': 'label-image'
                            }
                        }
                    }
                },
                'size': assetsPerPage,
                'from': skipAssets,
                'sort': [
                    {
                        'firstAddedToSearchIndexDatetime':
                            {
                                'unmapped_type': 'keyword',
                                'order': 'desc'
                            }
                    }
                ]
            })
        });
    }

    /**
	 * @param {number[]} assetIds
	 * @param {number} assetsPerPage
	 * @param {number} skipAssets
	 * @returns {Promise<Response>}
	 */
    fetchAssetsByAssetIds(
        assetIds,
        assetsPerPage,
        skipAssets
    )
    {
        return fetch(this._searchUri, {
            'method': 'POST',
            'headers': {
                'Content-Type': 'application/json'
            },
            'body': JSON.stringify({
                'track_total_hits': true,
                'query': {
                    'bool': {
                        'filter': [
                            {
                                'terms': {
                                    'assetId': assetIds
                                }
                            }
                        ],
                        'should': [
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetClass': 'image'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.png-max'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.jpeg-max'
                                            }
                                        }
                                    ]
                                }
                            },
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetClass': 'video'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.none'
                                            }
                                        }
                                    ]
                                }
                            },
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetClass': 'archive'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.none'
                                            }
                                        }
                                    ]
                                }
                            },
                            {
                                'bool': {
                                    'filter': [
                                        {
                                            'term': {
                                                'assetType.keyword': 'label-image'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.thumbnail-200'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.none'
                                            }
                                        },
                                        {
                                            'exists': {
                                                'field': 'assetFiles.vector-eps'
                                            }
                                        }
                                    ]
                                }
                            }
                        ],
                        'minimum_should_match': 1
                    }
                },
                'size': assetsPerPage,
                'from': skipAssets,
                'sort': [
                    {
                        'firstAddedToSearchIndexDatetime':
                            {
                                'unmapped_type': 'keyword',
                                'order': 'desc'
                            }
                    }
                ]
            })
        });
    }

    /**
	 * @param {string} filePath
	 * @returns {Promise<Response>}
	 */
    downloadAsset(filePath)
    {
        return fetch(this._downloadFileUri, {
            'method': 'POST',
            'headers': {
                'Content-Type': 'application/json'
            },
            'body': JSON.stringify({
                'referenceString': '1',
                'filePath': filePath,
            })
        });
    }

    /**
	 * @param {string[]} filePaths
	 * @param {string} archiveName
	 * @returns {Promise<Response>}
	 */
    downloadCollection(filePaths, archiveName)
    {
        return fetch(this._downloadArchiveUri, {
            'method': 'POST',
            'headers': {
                'Content-Type': 'application/json'
            },
            'body': JSON.stringify({
                'referenceString': '1',
                'filePathList': filePaths,
                'resultingFilename': archiveName
            })
        });
    }

    /**
	 * @param {string} jsonPath
	 * @returns {Promise<Response>}
	 */
    fetchProgressJson(jsonPath)
    {
        let getParam = '?' + Date.now();

        return fetch(jsonPath + getParam, {
            'method': 'GET',
            'cache': 'no-store'
        });
    }
}
